import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';
import { Users, UserPlus, Mail, KeyRound, Trash2, Loader2, Shield } from 'lucide-react';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

// Portal user management section rendered inside AccountDetailPanel.
// Lets an admin: invite additional users (branded email), send password
// resets, and remove a secondary email's portal access.
export default function PortalUsersSection({ account, onAccountUpdated }) {
  const { toast } = useToast();
  const [newEmail, setNewEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [resettingEmail, setResettingEmail] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Deduplicated list of portal-accessible emails
  const contactEmail = (account.contact_email || '').toLowerCase().trim();
  const secondaryEmails = (account.secondary_emails || [])
    .map((e) => e.toLowerCase().trim())
    .filter((e) => e && e !== contactEmail);
  const allEmails = [
    ...(contactEmail ? [{ email: contactEmail, primary: true }] : []),
    ...secondaryEmails.map((e) => ({ email: e, primary: false })),
  ];

  const handleInvite = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setSendingInvite(true);
    try {
      const res = await base44.functions.invoke('b2bInviteAdditionalUser', { account_id: account.id, email });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Invite failed');
      toast({ title: 'Invite sent', description: `Branded invite email sent to ${email}` });
      setNewEmail('');
      if (onAccountUpdated) {
        onAccountUpdated({ ...account, secondary_emails: res.data.secondary_emails, invited_at: new Date().toISOString(), portal_email: email });
      }
    } catch (e) {
      toast({ title: 'Invite failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setSendingInvite(false);
  };

  const handleReset = async (email) => {
    setResettingEmail(email);
    try {
      const res = await base44.functions.invoke('adminResetPassword', { email });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Reset failed');
      toast({ title: 'Password reset sent', description: `Reset email sent to ${email}` });
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setResettingEmail(null);
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      const updatedSecondary = secondaryEmails.filter((e) => e !== removeTarget);
      await base44.entities.B2BAccount.update(account.id, { secondary_emails: updatedSecondary });
      toast({ title: 'Access removed', description: `${removeTarget} can no longer access this portal` });
      if (onAccountUpdated) onAccountUpdated({ ...account, secondary_emails: updatedSecondary });
    } catch (e) {
      toast({ title: 'Remove failed', description: e.message, variant: 'destructive' });
    }
    setRemoving(false);
    setRemoveTarget(null);
  };

  return (
    <div>
      <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3 flex items-center gap-2">
        <Users className="w-3.5 h-3.5" /> PORTAL USERS ({allEmails.length})
      </p>

      {/* Invite additional user */}
      <div className="border border-white/10 bg-[#111115] p-4 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <UserPlus className="w-4 h-4 text-primary" />
          <p className="text-white/70 text-xs font-bold tracking-widest">INVITE ADDITIONAL USER</p>
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !sendingInvite) handleInvite(); }}
            placeholder="buyer@company.com"
            className="flex-1 bg-[#0d0d0f] border border-white/15 text-white text-xs px-3 py-2.5 outline-none focus:border-primary/50 transition-colors"
          />
          <button
            onClick={handleInvite}
            disabled={sendingInvite || !newEmail.trim()}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-4 py-2.5 transition-colors disabled:opacity-50"
          >
            {sendingInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            SEND INVITE
          </button>
        </div>
      </div>

      {/* Email list */}
      <div className="space-y-1.5">
        {allEmails.map(({ email, primary }) => (
          <div key={email} className="flex items-center gap-3 border border-white/10 bg-[#111115] px-4 py-3">
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-white/30 shrink-0" />
              <span className="text-white/80 text-xs truncate">{email}</span>
              {primary && (
                <span className="inline-flex items-center gap-1 border border-primary/30 bg-primary/10 text-primary text-[9px] font-bold tracking-widest px-1.5 py-0.5">
                  <Shield className="w-2.5 h-2.5" /> PRIMARY
                </span>
              )}
            </div>
            <button
              onClick={() => handleReset(email)}
              disabled={resettingEmail === email}
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
              title="Send password reset email"
            >
              {resettingEmail === email ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
              RESET
            </button>
            {!primary && (
              <button
                onClick={() => setRemoveTarget(email)}
                className="inline-flex items-center gap-1.5 border border-red-400/20 hover:border-red-400/40 text-red-400/70 hover:text-red-400 font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors"
                title="Remove portal access for this email"
              >
                <Trash2 className="w-3 h-3" /> REMOVE
              </button>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove portal access"
        description={`Are you sure you want to remove portal access for ${removeTarget}? This user will no longer be able to sign in to the trade portal for this account.`}
        confirmLabel="Remove Access"
        busy={removing}
        onConfirm={handleRemove}
        onClose={() => setRemoveTarget(null)}
      />
    </div>
  );
}